import React, { useState } from 'react';
import {
    Box, Button, Grid, TextField, MenuItem, Typography, CircularProgress
} from '@mui/material';
import { Description as InvoiceIcon } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader, { capitalizeFirst } from '../components/PrintAgencyHeader';
import { academyApi } from '../services/academyApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// boubaker 23/08/2026 - Page d'impression des factures
// (GenericPage type 'print' : filtres masquables + impression de la zone résultats)
export const PrintInvoicesPage: React.FC = () => {
    const { t } = useLanguage();

    const todayStr = new Date().toISOString().slice(0, 10);
    const monthStart = todayStr.slice(0, 8) + '01';
    const [dateFrom, setDateFrom] = useState(monthStart);
    const [dateTo, setDateTo] = useState(todayStr);
    const [status, setStatus] = useState<'all' | 'Paid' | 'Unpaid' | 'Partial'>('all');

    const [rows, setRows] = useState<any[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleLoad = async () => {
        setLoading(true);
        try {
            // boubaker 23/08/2026 - chargement serveur dédié impression (DLL Blobtic_Print : données JOINées)
            const res = await academyApi.getPrintInvoices(dateFrom, dateTo, status);
            setRows(res || []);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.start_date')} value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.end_date')} value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField select fullWidth size="small" label={t('academy.print.col_status')} value={status}
                    onChange={(e) => setStatus(e.target.value as any)}>
                    <MenuItem value="all">{t('academy.print.status_all')}</MenuItem>
                    <MenuItem value="Paid">{t('academy.print.status.Paid')}</MenuItem>
                    <MenuItem value="Unpaid">{t('academy.print.status.Unpaid')}</MenuItem>
                    <MenuItem value="Partial">{t('academy.print.status.Partial')}</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} md={1.5}>
                <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={handleLoad}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('academy.scheduler.load')}
                </Button>
            </Grid>
        </Grid>
    );

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties =>
        ({ border: '1px solid #999', padding: '3px 5px', textAlign: 'left', width, ...extra });

    const totalAmount = rows.reduce((a, b) => a + b.total_amount, 0);
    const totalPaid = rows.reduce((a, b) => a + b.amount_paid, 0);

    return (
        <GenericPage
            title={t('academy.print.invoices')}
            icon={<InvoiceIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
        >
            {!loaded ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    {t('academy.scheduler.not_loaded')}
                </Typography>
            ) : (
                <>
                    {/* Titre "Factures Août 2026" dérivé de la plage chargée */}
                    <PrintAgencyHeader title={`${capitalizeFirst(t('academy.menu.prints.invoices'))} ${capitalizeFirst(new Date(dateFrom + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }))}`} />

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={cellStyle(90)}>{t('academy.print.col_invoice')}</th>
                                <th style={cellStyle()}>{t('academy.print.col_student')}</th>
                                <th style={cellStyle()}>{t('academy.scheduler.print.col_group')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_issued')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_due')}</th>
                                <th style={cellStyle(70)}>{t('academy.print.col_total')}</th>
                                <th style={cellStyle(70)}>{t('academy.print.col_paid')}</th>
                                <th style={cellStyle(70)}>{t('academy.print.col_status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={8} style={{ ...cellStyle(), textAlign: 'center' }}>{t('academy.print.no_data')}</td></tr>
                            ) : rows.map(inv => (
                                    <tr key={inv.invoice_id ?? inv.invoice_number}>
                                        <td style={cellStyle(90, { fontWeight: 'bold' })}>{inv.invoice_number}</td>
                                        <td style={cellStyle()}>{inv.student_name || ''}</td>
                                        <td style={cellStyle()}>{inv.group_name || ''}</td>
                                        <td style={cellStyle(80)}>{String(inv.issue_date || '').slice(0, 10)}</td>
                                        <td style={cellStyle(80)}>{String(inv.due_date || '').slice(0, 10)}</td>
                                        <td style={cellStyle(70)}>{inv.total_amount}</td>
                                        <td style={cellStyle(70)}>{inv.amount_paid}</td>
                                        <td style={cellStyle(70)}>{t(`academy.print.status.${inv.status}`)}</td>
                                    </tr>
                                ))}
                            {rows.length > 0 && (
                                <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                    <td colSpan={5} style={{ ...cellStyle(), textAlign: 'right' }}>{t('academy.print.total')}</td>
                                    <td style={cellStyle(70)}>{totalAmount}</td>
                                    <td style={cellStyle(70)}>{totalPaid}</td>
                                    <td style={cellStyle(70)} />
                                </tr>
                            )}
                        </tbody>
                    </table>
                </>
            )}
        </GenericPage>
    );
};

export default PrintInvoicesPage;
